import moment from 'moment-timezone';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
     DEFAULT_WORKDAYS = 'Mon..Sat';
var dayLookup = {};

/**
 * Provides helper functions for working with working days.
 *
 * Specify the days of week to consider 'working' and add any holidays.
 *
 * example: Set working days
 * { workdays: 'mon..sat' } // monday through saturday (including saturday)
 * { workdays: ['mon', 'tue', 'wed', thur'] } // monday through thursday
 * { workdays: ['Monday', 'Wednesday', 'Friday'] // just as it says
 *
 * Use these as an argument when constructing the work calendar
 * new WorkCalendar({ workdays: 'mon..sat'});
 *
 * example: Set holidays
 *
 * Holidays can be set as month/day pairs, or functions
 * { holidays: ['12/25', 1/1'] }
 * { holidays: [function(date) { return findMyHolidays(date); }] }
 *
 * A day is considered a holiday if any of the specified holidays are true
 *
 * new WorkCalendar({workdays: 'mon..fri', holidays: ['1/1']});
 *
 * Do some calculations!
 *
 * // true if the date falls on a workday
 * workCalendar.isWorkday(someDate);
 * // Today if today is a workday. Otherwise rolls forward till it finds one.
 * workCalendar.closestWorkday(someDate, 5);
 * // never today, rolls forward till it finds the next N workdays
 * workCalendar.workdaysAfter(someDate, 5);
 * // never today, rolls backward till it finds the previous N workdays
 * workCalendar.workdaysBefore(someDate, 5);
 **/
function WorkCalendar(config) {
  config = config || {};
  this.workdays = parseWorkdays(config.workdays);
  this.holidays = parseHolidays(config.holidays);
}

WorkCalendar.prototype.isWorkday = function(date) {
  if (isUnset(date)) return false;
  date = asDateTime(date);

  var day = (date.getDate && date.getDate()) || (date.day && date.day());

  // Check if this is a holday
  if (this.workdays.indexOf(day) < 0) return false;
  return !this.isHoliday(date);
};

WorkCalendar.prototype.isHoliday = function(date) {
  for (var i = 0; i < this.holidays.length; i++) {
    if (this.holidays[i](date)) return true;
  }
  return false;
};

WorkCalendar.prototype.closestWorkday = function(date) {
  if (isUnset(date)) return undefined;
  date = asDateTime(date);

  if (this.isWorkday(date)) return date;
  return rollForwardWorkday(date, this);
};

WorkCalendar.prototype.workdaysAfter = function(date, days) {
  if (isUnset(date)) return undefined;
  date = asDateTime(date);

  for (var i = 0; i < days; i++ ){ date = rollForwardWorkday(date, this); }

  if (!this.isWorkday(date)) date = rollForwardWorkday(date, this);
  return date;
};

WorkCalendar.prototype.workdaysBefore = function(date, days) {
  if (isUnset(date)) return undefined;
  date = asDateTime(date);

  for (var i = 0; i < days; i++ ){ date = rollBackWorkday(date, this); }

  if (!this.isWorkday(date)) date = rollBackWorkday(date, this);
  return date;
};

// Utility functions
function rollForwardWorkday(date, calendar) {
  date = asDateTime(date);
  do { date = addDay(date); } while (!calendar.isWorkday(date));
  return date;
}

function rollBackWorkday(date, calendar) {
  date = asDateTime(date);
  do { date = subtractDay(date); } while (!calendar.isWorkday(date));
  return date;
}

function isUnset(value) {
  return value === null || value === undefined || isNaN(value);
}

function asDateTime(thing) {
  if (typeof moment !== 'undefined') {
    return moment(thing).clone();
  } else {
    if (typeof thing === 'string') {
      return new Date(Date.parse(thing));
    } else {
      return new Date(thing);
    }
  }
}

function addDay(date) {
  if (typeof moment !== 'undefined') {
    return moment(date).add(1, 'day');
  } else {
    return new Date(date.getTime() + (24 * 60 * 60 * 1000));
  }
}

function subtractDay(date) {
  if (typeof moment !== 'undefined') {
    return moment(date).subtract(1, 'day');
  } else {
    return new Date(date.getTime() - (24 * 60 * 60 * 1000));
  }
}

// Lookup the day index. Memoized to prevent molasis
function dayIdxLookup(stringOrNumber) {
  if (dayLookup[stringOrNumber]) return dayLookup[stringOrNumber];
  if (stringOrNumber === undefined) return undefined;
  if (stringOrNumber === null) return undefined;
  if (DAYS[stringOrNumber]) return dayLookup[stringOrNumber] = stringOrNumber;

  var i=0, day, found=false;
  for (;i < DAYS.length; i++) {
    day = DAYS[i];
    found = day.toLowerCase().indexOf(stringOrNumber.toString().toLowerCase()) === 0;
    if (found) break;
  }
  if (found) return dayLookup[stringOrNumber] = i;
}

// Parses the workdays as provided in the WorkCalendar constructor
// @param workdays - String describing range. e.g. 'mon..sat' -> Monday through Saturday
// @param workdays - Array(Day Names) - An array of day names that should be considered 'work days'
function parseWorkdays(workdays) {
  if (!workdays) workdays = DEFAULT_WORKDAYS;

  if (Array.isArray(workdays)) {
    return workdays.map(function(day) {
      return dayIdxLookup(day);
    });
  }

  // mon..sat
  var range = workdays.split('..'),
      start = dayIdxLookup(range[0]),
      finish = dayIdxLookup(range[1]);

  if (isUnset(start) || isUnset(finish)) {
    var msg = "Could not parse calendar dates";
    console.error(msg, workdays);
    throw msg;
  } else {
    var days = [];
    for (var i = start; i <= finish; i++) {
      days.push(0 + i);
    }
    return days;
  }
}

function parseHolidays(holidays) {
  var hols = [];

  (holidays || []).forEach(function(specOrFunction) {
    if (typeof specOrFunction === 'function') {
      hols.push(specOrFunction);
    } else {
      var parts = specOrFunction.split('/').map(function(i) { return parseInt(i, 10); });
      hols.push(function(date) {
        var month = (date.month ? date.month() : (date.getMonth() + 1)),
            day = (date.date ? date.date() : date.getDate());
        return !!(month === parts[0] && day === parts[1]);
      });
    }
  });
  return hols;
}

export default WorkCalendar;



/** WEBPACK FOOTER **
 ** ./javascripts/common/models/work-calendar.js
 **/